import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Upload,
  FileText,
  X,
  CheckCircle2,
  Loader2,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

export function UploadPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 50 Mo');
      return;
    }
    setFile(selectedFile);
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/projects/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      toast.success('Fichier uploadé avec succès');
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'upload';
      toast.error(message);
      setUploading(false);
    }
  };

  const handleClearFile = () => {
    setFile(null);
    setUploadProgress(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            Analyser un DCE
          </h1>
          <p className="text-slate-600 mt-2">
            Uploadez votre document PDF pour obtenir une analyse complète avec recommandation GO / NO GO.
          </p>
        </div>

        {user?.plan === 'novice' && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900">Plan Novice</p>
                <p className="text-sm text-slate-600">
                  Vous avez utilisé {user?.analyses_this_month || 0} analyse(s) sur 5 ce mois-ci.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-2 border-dashed border-slate-200">
          <CardContent className="p-8">
            {!file ? (
              <div
                className="flex flex-col items-center justify-center py-12"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                  <Upload className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  Glissez votre fichier ici
                </h3>
                <p className="text-slate-500 mb-6">ou</p>
                <label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button asChild data-testid="browse-btn">
                    <span className="cursor-pointer">Parcourir les fichiers</span>
                  </Button>
                </label>
                <p className="text-sm text-slate-400 mt-4">
                  PDF uniquement • Maximum 50 Mo • 500 pages max
                </p>
              </div>
            ) : (
              <div className="py-6">
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{file.name}</p>
                    <p className="text-sm text-slate-500">{formatFileSize(file.size)}</p>
                  </div>
                  {!uploading && (
                    <button
                      onClick={handleClearFile}
                      className="p-2 hover:bg-slate-200 rounded-lg"
                      data-testid="remove-file-btn"
                    >
                      <X className="w-5 h-5 text-slate-500" />
                    </button>
                  )}
                </div>

                {uploading && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-600">Upload en cours...</span>
                      <span className="text-sm font-medium text-slate-900">{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-2" />
                  </div>
                )}

                <div className="mt-6 flex gap-3">
                  {!uploading && (
                    <Button
                      variant="outline"
                      onClick={handleClearFile}
                      data-testid="cancel-btn"
                    >
                      Annuler
                    </Button>
                  )}
                  <Button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="flex-1 gap-2"
                    data-testid="upload-btn"
                  >
                    {uploading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Upload en cours...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Lancer l'analyse
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardContent className="p-6">
            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">
              Documents acceptés
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { name: 'DCE complet', desc: 'PDF unique ou archive' },
                { name: 'RC', desc: 'Règlement de Consultation' },
                { name: 'CCAP', desc: 'Cahier des Clauses Administratives' },
                { name: 'CCTP', desc: 'Cahier des Clauses Techniques' },
                { name: 'DPGF', desc: 'Décomposition du Prix Global' },
                { name: 'Programme', desc: 'Programme technique' },
              ].map((doc) => (
                <div key={doc.name} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900">{doc.name}</p>
                    <p className="text-sm text-slate-500">{doc.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
